/**
 * Vector helpers.
 *
 * Deliberately scalar-in / scalar-out where it matters: the hot paths run
 * 8 players x 30 ticks/s and allocating a vector per comparison is how a
 * server tick starts sawtoothing on GC.
 *
 * `Math.hypot` is never used — it is implementation-defined and slower than
 * `sqrt(x*x + z*z)`, which is correctly rounded everywhere.
 */
import { dcos, dsin } from "./math.js";
export function len2XZ(x, z) {
    return x * x + z * z;
}
export function lenXZ(x, z) {
    return Math.sqrt(x * x + z * z);
}
export function dist2XZ(ax, az, bx, bz) {
    const dx = ax - bx;
    const dz = az - bz;
    return dx * dx + dz * dz;
}
export function distXZ(ax, az, bx, bz) {
    return Math.sqrt(dist2XZ(ax, az, bx, bz));
}
export function dist2(ax, ay, az, bx, by, bz) {
    const dx = ax - bx;
    const dy = ay - by;
    const dz = az - bz;
    return dx * dx + dy * dy + dz * dz;
}
export function dist3(ax, ay, az, bx, by, bz) {
    return Math.sqrt(dist2(ax, ay, az, bx, by, bz));
}
/**
 * Facing, in the camera's frame: forward is AWAY from the camera.
 *
 * This was the wrong sign, and it is the single worst bug the rewrite shipped:
 * W drove you backwards and every ability fired behind you.
 *
 * The camera orbits to `player + (sin(yaw), ·, cos(yaw)) * distance` and looks
 * back at the player (`CameraController.js:205-207`), so at yaw 0 the camera is
 * at +Z and "into the screen" is −Z. Returning (+sin, +cos) pointed straight at
 * the lens. v1 had it right — `getCameraDirections()` is (−sin, −cos) — and v2's
 * own free-fly camera still uses that convention, so the sim was disagreeing
 * with the camera inside one codebase.
 *
 * `rightFromYaw` is unchanged and already matched v1: it is this vector rotated
 * −90°, which is only true with the negated forward.
 */
export function forwardFromYaw(yaw) {
    return { x: -dsin(yaw), z: -dcos(yaw) };
}
/** The caster's right-hand direction: `forwardFromYaw` rotated −90°. */
export function rightFromYaw(yaw) {
    return { x: dcos(yaw), z: -dsin(yaw) };
}
/**
 * Squared distance from a point to an oriented rectangle, in the XZ plane.
 *
 * The rectangle's spine is the segment a->b; `halfWidth` extends it sideways.
 * This is the line-ability hit shape: `pointSegmentDist2XZ <= halfWidth + pad`
 * put a radial cap of the FULL half-width on the segment's end, so a 3m-wide
 * Fault Line reached 1.5m past its drawn tip. Against a rectangle the tip
 * over-delivers by only the body pad — the same grammar as the sides.
 *
 * A zero-length spine (a blink swallowed whole by a wall) degrades to a disc
 * of radius `halfWidth`: with no direction left there is no long axis, but the
 * width the ability promises still exists around the caster.
 */
export function pointRectDist2XZ(px, pz, ax, az, bx, bz, halfWidth) {
    const abx = bx - ax;
    const abz = bz - az;
    const abLen = Math.sqrt(abx * abx + abz * abz);
    if (abLen <= 1e-6) {
        const d = distXZ(px, pz, ax, az) - halfWidth;
        return d > 0 ? d * d : 0;
    }
    const ux = abx / abLen;
    const uz = abz / abLen;
    const dx = px - ax;
    const dz = pz - az;
    const along = dx * ux + dz * uz;
    const lateral = Math.abs(dx * uz - dz * ux);
    const dAlong = along < 0 ? -along : along > abLen ? along - abLen : 0;
    const dLat = lateral > halfWidth ? lateral - halfWidth : 0;
    return dAlong * dAlong + dLat * dLat;
}
/** Squared distance from a point to a segment, in the XZ plane. */
export function pointSegmentDist2XZ(px, pz, ax, az, bx, bz) {
    const abx = bx - ax;
    const abz = bz - az;
    const abLen2 = abx * abx + abz * abz;
    if (abLen2 <= 1e-12)
        return dist2XZ(px, pz, ax, az);
    let t = ((px - ax) * abx + (pz - az) * abz) / abLen2;
    t = t < 0 ? 0 : t > 1 ? 1 : t;
    const cx = ax + abx * t;
    const cz = az + abz * t;
    return dist2XZ(px, pz, cx, cz);
}
//# sourceMappingURL=vec.js.map